import { useCallback } from 'react';
import { useIntl } from 'react-intl';

import type { ApiWorkflowDialogMessages } from '@verantos-dev/shared-components';

import { useCohortPickerOptions } from './useCohortPickerOptions';
import { useCohortPickerSelection } from './useCohortPickerSelection';
import { useConfirmCohortPick } from './useConfirmCohortPick';
import { selectCohortProfileId } from '../state/selectors';
import { useAnalysisDefinitionStore } from '../state/useAnalysisDefinitionStore';
import type { UseAddCohortPickerModalArgs, UseAddCohortPickerModalResult } from '../types';

export const useAddCohortPickerModal = ({
  projectId,
  onClose,
}: UseAddCohortPickerModalArgs): UseAddCohortPickerModalResult => {
  const intl = useIntl();

  const currentCohortId = useAnalysisDefinitionStore(selectCohortProfileId);

  const {
    cohortList,
    allOptions,
    options,
    searchQuery,
    onSearchChange,
    isLoading,
    isError,
    emptyMessage,
    noResultsMessage,
  } = useCohortPickerOptions(projectId);

  const { selectedId, onSelect } = useCohortPickerSelection({ currentCohortId, allOptions });

  const { onConfirm, canConfirm } = useConfirmCohortPick({
    projectId,
    cohortList,
    selectedId,
    currentCohortId,
    isLoading,
    onClose,
  });

  const formatSubjectCount = useCallback(
    (count: number) =>
      intl.formatMessage({ id: 'analysisDetail.addCohortModal.subjects' }, { count }),
    [intl]
  );

  const dialogMessages: ApiWorkflowDialogMessages = {
    entrySaveButtonText: intl.formatMessage({ id: 'analysisDetail.addCohortModal.addConfirm' }),
    entryCancelButtonText: intl.formatMessage({ id: 'analysisDetail.addCohortModal.cancel' }),
  };

  return {
    title: intl.formatMessage({ id: 'analysisDetail.addCohortModal.addTitle' }),
    selectHeading: intl.formatMessage(
      { id: 'analysisDetail.addCohortModal.selectHeading' },
      { count: options.length }
    ),
    searchPlaceholder: intl.formatMessage({
      id: 'analysisDetail.addCohortModal.searchPlaceholder',
    }),
    emptyMessage,
    noResultsMessage,
    searchQuery,
    onSearchChange,
    options,
    selectedId,
    onSelect,
    onConfirm,
    onCancelClose: onClose,
    isLoading,
    isError,
    canConfirm,
    formatSubjectCount,
    dialogMessages,
  };
};
